/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { parseFieldIdentifier, DFSValueType, NIFormData } from './form/services';
import { rescopeDataPath } from './form';
import { Button, Theme } from "@material-ui/core";
import BackIcon from "@material-ui/icons/ArrowBackIos";
import { createStyles, makeStyles } from "@material-ui/styles";
import { ErrorObject } from "ajv";
import equal from "fast-deep-equal";
import { has } from "json-pointer";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import { createSelectorCreator, defaultMemoize } from "reselect";
import { combineLatest, Observable } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";
import { AppState, followState, FollowUpVisitData, submitFollowUp, syncSystem, updateFollowUp, redirect } from "../../redux";
import { FormWrapper } from './form/components/Form';
import SubmitOverlay from "./SubmitOverlay";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
      overflow: "hidden"
    },
    navLinks: {
      backgroundColor: theme.palette.primary.main,
      alignItems: "stretch"
    },
    link: {
      display: "flex",
      height: "40px",
      marginLeft: theme.spacing(2)
    },
    editorOverlayPaper: {
      height: "100%"
    },
    editorOverlay: {
      height: "calc(100%)"
    },
    tocFooter: {
      display: "flex",
      flexDirection: "column"
    },
    tocFooterButton: {
      margin: theme.spacing(1),
      display: "flex",
      justifyContent: "flex-end",
      height: "30px",
      alignItems: "center",
      textTransform: "none"
    },
    submitButton: {
      verticalAlign: "middle",
      height: "48px",
      borderRadius: 0
    },
    syncStatusText: {
      // textDecoration: "underline",
      marginRight: theme.spacing(1)
    },
    backButton: {
      color: theme.palette.getContrastText(theme.palette.primary.main),
      textDecoration: "none"
    },


    dialog: {

    }
  })
);

const selectSubmitState = createSelectorCreator(defaultMemoize, equal)(
  (state: AppState) => state.followUps.submitState,
  state => state,
);

interface FollowUpEditorProps extends RouteComponentProps<{ facilityId: string, planId: string }> { }

const FollowUpEditor: React.FC<FollowUpEditorProps> = props => {
  const facilityId = decodeURIComponent(props.match.params.facilityId);
  const followUpId = decodeURIComponent(props.match.params.planId);

  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [data$, setData$] = useState<Observable<NIFormData>>();
  const [dirty, setDirty] = useState(false);

  const store = useStore();
  const dispatch = useDispatch();

  const followUp = useSelector(
    (state: AppState) => state.followUps.items.get(facilityId)?.find(fu => fu.data.id === followUpId) || null,
    (a, b) => equal(a, b)
  );
  const visitData: Partial<FollowUpVisitData> | null = useSelector((state: AppState) => {
    return state.followUps.visits.get(followUpId) ?? null;
  });

  const form = useSelector(
    (state: AppState) => {
      if (!followUp) return null;

      const versions = state.forms.items[followUp.type];
      if (!versions) return null;

      const latestVersion = Object.keys(versions).map(version => Number.parseInt(version)).sort().pop()!;
      return versions[latestVersion];
    }
  );

  const formId = form?.id;

  useEffect(
    function loadFollowUp() {
      if (!formId) {
        setIsLoaded(false);
        dispatch(syncSystem());
        return;
      }

      setData$(
        combineLatest(
          [followState(store).pipe(
            map(state => state.followUps.items.get(facilityId)?.find(fu => fu.data.id === followUpId)?.data ?? null),
          ),
          followState(store).pipe(
            map(state => state.followUps.visits.get(followUpId) ?? null),
          ),
          ]).pipe(
          map(([actionPlan, visit]) => ({ actionPlan, visit })),
          distinctUntilChanged((a, b) => equal(a, b)),
          // tap(newData => console.log('New Data', newData)),
        )
      );

      setIsLoaded(true);
    },
    [facilityId, followUpId, formId, dispatch, store]
  );

  const latestData = useRef<NIFormData | null>();

  useEffect(function trackData() {
    if (!data$) return;

    const subscription = data$.subscribe(
      newData => (latestData.current = newData)
    );

    return () => subscription.unsubscribe();
  });

  const classes = useStyles({});

  const [errors, setErrors] = useState<number>(0);

  const canSubmit = useMemo(() => {
    return visitData && errors === 0;
  }, [errors, visitData]);

  const doSubmit = useCallback(() => {
    if (!visitData || !followUp) {
      alert(
        "Error. Please refresh and try again after a few moments. If problem persists please contact support"
      );
      return;
    }

    dispatch(submitFollowUp.request({ id: followUpId, type: followUp.type }));
  }, [dispatch, visitData, followUp, followUpId]);

  const footer = useMemo(() => {
    return (
      <div className={classes.tocFooter}>
        <Button
          disabled={!canSubmit}
          fullWidth
          color="secondary"
          className={classes.submitButton}
          variant="contained"
          onClick={doSubmit}
        >
          Submit
        </Button>
      </div>
    );
  }, [classes, canSubmit, doSubmit]);

  const handleSetData = useMemo(
    () => (path: string, scope: string, newValue: DFSValueType) => {

      const resolvedPath = parseFieldIdentifier(rescopeDataPath(path, scope));
      if (newValue !== null) {
        setDirty(true);
        dispatch(
          updateFollowUp(
            followUpId,
            {
              op: 'add',
              path: resolvedPath,
              value: newValue
            }
          )
        );
      }
      else if (has(latestData.current!, resolvedPath)) {
        setDirty(true);
        dispatch(updateFollowUp(
          followUpId,
          {
            op: 'remove',
            path: resolvedPath
          }
        ));
      }
    },
    [latestData, dispatch, followUpId, setDirty]
  );

  const handleRemoveData = useMemo(
    () => (path: string, scope: string) => {
      setDirty(true);
      const resolvedPath = rescopeDataPath(path, scope);
      if (has(latestData.current!, parseFieldIdentifier(resolvedPath))) {
        dispatch(updateFollowUp(followUpId, {
          op: 'remove',
          path: resolvedPath,
        }));
      }
    },
    [latestData, dispatch, followUpId, setDirty]
  );

  const submitState = useSelector(selectSubmitState);

  if (!isLoaded || !form) {
    return <div />;
  }

  return (
    <div className={classes.root}>
      <FormWrapper
        form={form!}
        data$={data$!}
        evalID={'not-a-concern'}  // This is used by image handling controls so as long as action plans don't record images we're fine
        compressedToCHeader={
          <div>
            <Link className={classes.backButton} to={`/follow-ups/${facilityId}`}>
              <Button className={classes.backButton}>
                <BackIcon />
                Back
              </Button>
            </Link>
            {/* <div className={classes.navLinks}>
              <NavLinks orientation="drawer" />
            </div> */}
          </div>
        }
        onErrors={(errors: ErrorObject[]) => setErrors(errors.length)}
        onSetData={handleSetData}
        onRemoveData={handleRemoveData}
        imageService={null as any} // This is used for image handling. Per above evalID comment, we should not use images in follow-ups
        // TODO: Implement expression result tracking and validity
        tocFooter={footer}
      />

      <SubmitOverlay
        {...submitState}
        history={props.history}
        onFinishedPrompts={() => { }}  // this shouldn't matter
        onSubmitted={() => {
          dispatch(redirect(`/follow-ups/${facilityId}`, props.history))
        }}
      />
    </div>
  );
};

export default FollowUpEditor;
